import { ReactElement, useMemo } from 'react'
import { useBFSM, useBFSMState } from '../../contexts/bfsmContext'
import { AdminCancelBookTemplate } from './admincancelbook.template'
import { useAppSelector } from '../../redux/hooks'
import { getBookingId } from '../../redux/reducers/appSettings'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import {
    CancelBookingAdminActions,
    CancelBookingAdminState,
} from '@raskrask-dk/bookingflow-state-machine'
import { FeeSizeOptionsTypeLiteral } from './admincancelbook.props'

const feeSizeOptions = [
    { label: '0%', value: '0' },
    { label: '50%', value: '50' },
    { label: '100%', value: '100' },
] as FeeSizeOptionsTypeLiteral[]

const AdminCancelBook = (): ReactElement => {
    const [BFSM] = useBFSM()

    const [cancellationFeeSize, setCancellationFeeSize] = useBFSMValue<
        CancelBookingAdminState,
        CancelBookingAdminActions.SetCancellationFeeSize,
        'cancellationFeeSize'
    >('cancellationFeeSize', CancelBookingAdminActions.SetCancellationFeeSize)
    const [shouldSendCustomerMessages, setShouldSendCustomerMessages] =
        useBFSMValue<
            CancelBookingAdminState,
            CancelBookingAdminActions.SetShouldSendCustomerMessages,
            'shouldSendCustomerMessages'
        >(
            'shouldSendCustomerMessages',
            CancelBookingAdminActions.SetShouldSendCustomerMessages
        )
    const [shouldSendPartnerMessages, setShouldSendPartnerMessages] =
        useBFSMValue<
            CancelBookingAdminState,
            CancelBookingAdminActions.SetShouldSendPartnerMessages,
            'shouldSendPartnerMessages'
        >(
            'shouldSendPartnerMessages',
            CancelBookingAdminActions.SetShouldSendPartnerMessages
        )
    const [shouldCloseTime, setShouldCloseTime] = useBFSMValue<
        CancelBookingAdminState,
        CancelBookingAdminActions.SetShouldCloseTime,
        'shouldCloseTime'
    >('shouldCloseTime', CancelBookingAdminActions.SetShouldCloseTime)
    const [cancelOnBehalfOf, setCancelOnBehalfOf] = useBFSMValue<
        CancelBookingAdminState,
        CancelBookingAdminActions.SetCancelOnBehalfOf,
        'cancelOnBehalfOf'
    >('cancelOnBehalfOf', CancelBookingAdminActions.SetCancelOnBehalfOf)
    const [cancelReason, setCancelReason] = useBFSMValue<
        CancelBookingAdminState,
        CancelBookingAdminActions.SetCancelReason,
        'cancelReason'
    >('cancelReason', CancelBookingAdminActions.SetCancelReason)

    const bookingId = useAppSelector((state) => getBookingId(state))

    const currentState = useBFSMState()
    const flowType = BFSM.flowType
    const shouldShowAdminSection = useMemo(() => {
        return flowType === 'AdminCancelBooking'
    }, [flowType, currentState.type])

    const feeValue = feeSizeOptions.find(
        (o) => o.value === cancellationFeeSize?.toString()
    )
    const canDoCTA =
        bookingId !== undefined &&
        cancellationFeeSize !== undefined &&
        shouldSendCustomerMessages !== undefined &&
        shouldSendPartnerMessages !== undefined &&
        shouldCloseTime !== undefined &&
        cancelOnBehalfOf !== undefined &&
        cancelReason !== undefined &&
        BFSM.canDo(CancelBookingAdminActions.Next)

    const onClickCTA = async () => {
        BFSM.doAction(CancelBookingAdminActions.Next, undefined)
    }

    const templateProps = {
        settingsHandler: {
            cancellationFeeSize: cancellationFeeSize,
            setCancellationFeeSize: setCancellationFeeSize,
            shouldSendCustomerMessages: shouldSendCustomerMessages,
            setShouldSendCustomerMessages: setShouldSendCustomerMessages,
            shouldSendPartnerMessages: shouldSendPartnerMessages,
            setShouldSendPartnerMessages: setShouldSendPartnerMessages,
            shouldCloseTime: shouldCloseTime,
            setShouldCloseTime: setShouldCloseTime,
            cancelOnBehalfOf: cancelOnBehalfOf,
            setCancelOnBehalfOf: setCancelOnBehalfOf,
            cancelReason: cancelReason,
            setCancelReason: setCancelReason,
        },
        ctaHandle: {
            isDisabled: !canDoCTA,
            onClickCta: onClickCTA,
        },
        adminSectionHandle: {
            shouldShowAdminSection: shouldShowAdminSection,
        },
        feeSizeOptions: feeSizeOptions,
        feeValue: feeValue,
        bookingId: bookingId || '',
    }

    return <AdminCancelBookTemplate {...templateProps} />
}
export default AdminCancelBook
